import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import TenantPickerModal from '../Tenants/TenantPickerModal';
import { useTenants } from '../../hooks/tenants/useTenants';
import { UseParkingQueueReturn } from '../../hooks/parkingQueue';
import { useCustomerType } from '../../hooks/useCustomerType';
import { defaultMarkedInternal, residenceOf, SpotEntryPrefill } from '../../Helpers/parkingQueueUtils';
import { todayStockholm } from '../../Helpers/leaseUtils';
import { parkingAreaDisplayName } from '../../Helpers/parkingUtils';
import { translate } from '../../types/EnumTranslations';
import { ParkingSpotType, TenantKind } from '../../types/Enums';
import { ParkingQueueEntry, ParkingQueueEntryInput, QUEUE_WISH_LABELS, QueueWish } from '../../types/parkingQueue';
import { PII_HINT, wantedAreasText } from './queueLabels';

interface Props {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: ParkingQueueEntryInput) => Promise<void>;
    /** When given, the modal edits this entry; otherwise it creates one. */
    entry?: ParkingQueueEntry;
    /** Create only: wishes the form starts with (from a spot's "Lägg till i kön"). */
    prefill?: SpotEntryPrefill;
    queue: UseParkingQueueReturn;
    submitting?: boolean;
    error?: string | null;
}

type Wish = QueueWish | 'any';
interface FormValues {
    tenantKey: string;
    queueDate: string;
    wantedAreas: string[]; // "buildingKey/areaKey"
    spotTypes: ParkingSpotType[];
    wantsCharger: Wish;
    wantsOutlet: Wish;
    markedInternal: boolean;
    comment: string;
}

const schema = Yup.object({
    tenantKey: Yup.string().required('Välj sökande'),
    queueDate: Yup.string().required('Ange ködatum'),
    wantedAreas: Yup.array().of(Yup.string()).min(1, 'Välj minst ett område'),
});

const SPOT_TYPES = Object.values(ParkingSpotType);

/** Create/edit a queue entry (Formik + Yup). The applicant is picked (or created) in TenantPickerModal; it is fixed once the entry exists. */
const ParkingQueueEntryModal: React.FC<Props> = ({ show, onHide, onSubmit, entry, prefill, queue, submitting, error }) => {
    const { tenantsByKey, getTenantName } = useTenants();
    const customerType = useCustomerType();
    const [showTenantPicker, setShowTenantPicker] = useState(false);
    // "Står i kö" on tenants that already have an active entry; informational, never blocking.
    const queueBadges = useMemo(() => {
        const badges: { [tenantKey: string]: string } = {};
        queue.entries.filter(e => e.status === 'active').forEach(e => { badges[e.tenantKey] = 'Står i kö'; });
        return badges;
    }, [queue.entries]);

    const initial: FormValues = useMemo(() => ({
        tenantKey: entry?.tenantKey ?? '',
        queueDate: entry?.queueDate ?? todayStockholm(),
        wantedAreas: Object.entries(entry?.wantedAreas ?? prefill?.wantedAreas ?? {}).map(([a, b]) => `${b}/${a}`),
        spotTypes: entry ? (Object.keys(entry.spotTypes ?? {}) as ParkingSpotType[]) : (prefill?.spotTypes ?? []),
        wantsCharger: (entry ? entry.wantsCharger : prefill?.wantsCharger) ?? 'any',
        wantsOutlet: (entry ? entry.wantsOutlet : prefill?.wantsOutlet) ?? 'any',
        markedInternal: entry ? entry.markedInternal === true : defaultMarkedInternal(customerType),
        comment: entry?.comment ?? '',
    }), [entry, prefill, customerType]);

    const buildingsWithAreas = useMemo(() =>
        Object.entries(queue.areasByBuilding)
            .map(([b, areas]) => ({ buildingKey: b, name: queue.buildingName(b), areas: Object.values(areas).sort((x, y) => x.name.localeCompare(y.name, 'sv')) }))
            .filter(b => b.areas.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        [queue]);

    const toInput = (v: FormValues): ParkingQueueEntryInput => {
        const wantedAreas: { [areaKey: string]: string } = {};
        v.wantedAreas.forEach(pair => { const [b, a] = pair.split('/'); wantedAreas[a] = b; });
        return {
            tenantKey: v.tenantKey,
            queueDate: v.queueDate,
            wantedAreas,
            spotTypes: v.spotTypes.length > 0 ? v.spotTypes : undefined,
            wantsCharger: v.wantsCharger === 'any' ? undefined : v.wantsCharger,
            wantsOutlet: v.wantsOutlet === 'any' ? undefined : v.wantsOutlet,
            comment: v.comment.trim() || undefined,
            markedInternal: v.markedInternal,
        };
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Formik<FormValues> initialValues={initial} validationSchema={schema} enableReinitialize onSubmit={async v => { await onSubmit(toInput(v)); }}>
                {({ values, errors, touched, handleSubmit, setFieldValue, handleChange }) => {
                    const existing = !entry && values.tenantKey
                        ? queue.entries.filter(e => e.status === 'active' && e.tenantKey === values.tenantKey) : [];
                    const onTenantChange = (tenantKey: string) => setFieldValue('tenantKey', tenantKey);
                    const residence = values.tenantKey ? residenceOf(values.tenantKey, queue.leases) : null;
                    const wishRadios = (field: 'wantsCharger' | 'wantsOutlet', label: string) => (
                        <Form.Group className="mb-3">
                            <Form.Label>{label}</Form.Label>
                            <div>
                                {(['yes', 'no', 'any'] as Wish[]).map(w => (
                                    <Form.Check inline key={w} type="radio" id={`${field}-${w}`} name={field} label={QUEUE_WISH_LABELS[w]}
                                        checked={values[field] === w} onChange={() => setFieldValue(field, w)} />
                                ))}
                            </div>
                        </Form.Group>
                    );
                    return (
                        <>
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton><Modal.Title>{entry ? 'Redigera köplats' : 'Ny köplats'}</Modal.Title></Modal.Header>
                            <Modal.Body>
                                {error && <Alert variant="danger">{error}</Alert>}
                                <Form.Group className="mb-3">
                                    <Form.Label>Sökande *</Form.Label>
                                    {values.tenantKey ? (
                                        <div className="p-3 border rounded bg-light d-flex align-items-center justify-content-between">
                                            <div>
                                                <strong>{getTenantName(values.tenantKey)}</strong>
                                                <div className="text-muted small">
                                                    {tenantsByKey[values.tenantKey]?.kind === TenantKind.Company ? 'Företag' : 'Privatperson'}
                                                    {(tenantsByKey[values.tenantKey]?.email || tenantsByKey[values.tenantKey]?.phone) && ` · ${tenantsByKey[values.tenantKey]?.email || tenantsByKey[values.tenantKey]?.phone}`}
                                                </div>
                                            </div>
                                            {!entry && <Button variant="link" size="sm" onClick={() => setShowTenantPicker(true)}>Ändra</Button>}
                                        </div>
                                    ) : (
                                        <div>
                                            <Button variant="outline-primary" onClick={() => setShowTenantPicker(true)}>Välj sökande…</Button>
                                        </div>
                                    )}
                                    {touched.tenantKey && errors.tenantKey && <div className="text-danger small mt-1">{errors.tenantKey}</div>}
                                </Form.Group>
                                {existing.length > 0 && (
                                    <Alert variant="info">
                                        {getTenantName(values.tenantKey)} har redan {existing.length} köplats{existing.length > 1 ? 'er' : ''}:{' '}
                                        {existing.map(e => `${wantedAreasText(e.wantedAreas, queue)}, ${e.queueDate}`).join('; ')}
                                    </Alert>
                                )}
                                <Form.Group className="mb-3">
                                    <Form.Label>Ködatum *</Form.Label>
                                    <Form.Control type="date" name="queueDate" value={values.queueDate} onChange={handleChange} isInvalid={touched.queueDate && !!errors.queueDate} />
                                    <Form.Text muted>Kan sättas bakåt i tiden; loggas</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Önskade områden *</Form.Label>
                                    {buildingsWithAreas.length === 0 && <div className="text-muted">Inga parkeringsområden finns registrerade.</div>}
                                    {buildingsWithAreas.map(b => (
                                        <div key={b.buildingKey} className="mb-2">
                                            <div className="fw-semibold">{b.name}</div>
                                            {b.areas.map(area => {
                                                const id = `${b.buildingKey}/${area.key}`;
                                                return (
                                                    <Form.Check key={id} type="checkbox" id={`area-${id}`} label={parkingAreaDisplayName(area)}
                                                        checked={values.wantedAreas.includes(id)}
                                                        onChange={e => setFieldValue('wantedAreas', e.target.checked ? [...values.wantedAreas, id] : values.wantedAreas.filter(x => x !== id))} />
                                                );
                                            })}
                                        </div>
                                    ))}
                                    {touched.wantedAreas && typeof errors.wantedAreas === 'string' && <div className="text-danger small">{errors.wantedAreas}</div>}
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Platstyp</Form.Label>
                                    <div>
                                        {SPOT_TYPES.map(t => (
                                            <Form.Check inline key={t} type="checkbox" id={`spotType-${t}`} label={translate(t)} checked={values.spotTypes.includes(t)}
                                                onChange={e => setFieldValue('spotTypes', e.target.checked ? [...values.spotTypes, t] : values.spotTypes.filter(x => x !== t))} />
                                        ))}
                                    </div>
                                </Form.Group>
                                {wishRadios('wantsCharger', 'Laddplats')}
                                {wishRadios('wantsOutlet', 'Motorvärmare')}
                                <Form.Group className="mb-3">
                                    <Form.Label>Intern eller extern</Form.Label>
                                    <div className="small mb-2">
                                        {!values.tenantKey
                                            ? <span className="text-muted">Välj sökande först.</span>
                                            : residence
                                                ? <>Bor i / har lokal i <strong>{queue.buildingName(residence.buildingKey)} · {residence.label}</strong> enligt hyresavtal – räknas som intern.</>
                                                : <span className="text-muted">Inget pågående eller kommande hyresavtal för lägenhet eller lokal.</span>}
                                    </div>
                                    <Form.Check type="checkbox" id="markedExternal" name="markedExternal"
                                        label="Räknas som extern"
                                        checked={!values.markedInternal && !residence} disabled={!!residence}
                                        onChange={e => setFieldValue('markedInternal', !e.target.checked)} />
                                    <Form.Text muted>För sökande som varken bor eller har lokal i beståndet. Lämna rutan tom för den som gör det utan hyresavtal att visa, t.ex. medlemmar i en brf. Ändringar loggas.</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-1">
                                    <Form.Label>Kommentar</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="comment" value={values.comment} onChange={handleChange} />
                                    <Form.Text muted>{PII_HINT}</Form.Text>
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={onHide} disabled={submitting}>Avbryt</Button>
                                <Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Sparar…' : 'Spara'}</Button>
                            </Modal.Footer>
                        </Form>
                        {/* Outside <Form>: React bubbles the picker's own submit through the portal to the enclosing form. */}
                        <TenantPickerModal show={showTenantPicker} onHide={() => setShowTenantPicker(false)} title="Välj sökande"
                            badges={queueBadges} onPick={tenant => onTenantChange(tenant.key)} />
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default ParkingQueueEntryModal;
